import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatAmountWithCommas } from '@unified-checkout/design-system';
import type { ITransactionsDateRange } from '@app/dashboard/types/innerLayout/transactions';
import type { IMetrics } from '@app/dashboard/types/innerLayout/metrics';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { useLangStore } from '@app/dashboard/store/lang';
import { getPayMeMetricCardsData } from '@app/dashboard/services/inner_layout/payme/metric-cards';
import { Metrics } from '@app/dashboard/components/inner_layout/Metrics';
import styles from './transactions-metrics-box.module.scss';

export const TransactionsMetricsBox = ({
  handle,
  dateRange,
  comparedRangeText,
  selectedBranch,
  selectedCurrency,
}: {
  handle: string;
  dateRange: ITransactionsDateRange;
  comparedRangeText: string;
  selectedBranch: { id: string | null; name: string | null } | null;
  selectedCurrency: string | null;
}) => {
  const { lang } = useLangStore();
  const { isLive } = useTestLiveModeStore();
  const { t } = useTranslation();

  const {
    isLoading,
    isError,
    data: metricsData,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_PAYME_TRANSACTIONS_METRIC_CARDS_DATA,
      dateRange,
      selectedBranch,
      selectedCurrency,
      isLive,
    ],
    queryFn: () =>
      getPayMeMetricCardsData({
        handle,
        isLive,
        dateRange,
        branch: selectedBranch?.id === 'Main' ? 'Main' : selectedBranch?.id || undefined,
        currency: selectedCurrency,
      }),
    // Don't trigger API call until both date range and currency are set
    enabled:
      Object.values(dateRange || {}).every((value) => !!value) &&
      !!selectedCurrency,
  });

  const metricCardsData: IMetrics['data'] = (() => {
    const data = metricsData?.data?.data || metricsData?.data || {};
    const { total_revenue, total_transactions, refunded_void_transactions } = data;

    const ar = lang === 'ar';

    return [
      {
        // Currency for total revenue
        ...(
          (total_revenue?.en_currency && total_revenue?.ar_currency) ?
            { currency: ar ? total_revenue.ar_currency : total_revenue.en_currency }
            :
            {}
        ),
        title: total_revenue ?
          (ar ? total_revenue.ar_title : total_revenue.en_title) :
          t('innerLayout.paymePage.metrics.totalRevenue'),
        value: (total_revenue?.en_currency && total_revenue?.ar_currency) ?
          formatAmountWithCommas({ amount: total_revenue.value || 0, keepDecimals: true, currency: total_revenue.en_currency, excludeZeroDecimals: true }) : (total_revenue?.value || 0),
      },
      {
        title: total_transactions ? (ar ? total_transactions.ar_title : total_transactions.en_title) : t('innerLayout.paymePage.metrics.totalTransactions'),
        value: total_transactions?.value || 0,
      },
      {
        title: refunded_void_transactions ?
          (ar ? refunded_void_transactions.ar_title : refunded_void_transactions.en_title) :
          t('innerLayout.paymePage.metrics.refundsVoids'),
        value: refunded_void_transactions?.value || 0,
      },
    ];
  })();

  return (
    <div className={styles['transactions-metrics-box']}>
      <Metrics
        comparisonRangeName={comparedRangeText || ''}
        data={metricCardsData.length ? metricCardsData : undefined}
        noOfCards={3}
        isError={isError}
        isLoading={isLoading}
      />
    </div>
  );
};

